export function adminDbAccess(args) {
  const {
    creating = false,
    allowlistEntries = [],
    allowlistUsable = false,
    accessRequested = false,
  } = args || {};

  // Get active tab from URL query param
  const urlParams = new URLSearchParams(window.location.search);
  const tabFromUrl = urlParams.get("tab") || "db_users";

  return {
    creating,
    activeTab: tabFromUrl,
    allowlistUsable,
    editingAllowlist: false,
    saving: false,
    allowlistEntries,
    currentRules: [], // The current state of rules being edited
    accessRequested,
    requestingAccess: false,
    MAX_RULES_PER_CUSTOMER: 5,

    // Set active tab and update URL
    setActiveTab(tab) {
      this.activeTab = tab;
      const url = new URL(window.location);
      url.searchParams.set("tab", tab);
      window.history.pushState({}, "", url);
    },

    async requestAccess() {
      this.requestingAccess = true;

      try {
        const response = await fetch(
          "/request_product_access?key=datacloud_ip_restriction",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": document
                .querySelector('meta[name="csrf-token"]')
                .getAttribute("content"),
            },
          },
        );

        if (response.ok) {
          this.accessRequested = true;
          if (typeof pushNotification === "function") {
            window.pushNotification({
              title: "Access requested",
              desc: "We'll review your request and get back to you shortly.",
            });
          }
        } else {
          throw new Error("Failed to request access");
        }
      } catch (error) {
        console.error("Error requesting access:", error);
        if (typeof pushNotification === "function") {
          window.pushNotification({
            title: "Error",
            desc: "An error occurred while requesting access. Please try again.",
            error: true,
          });
        }
      } finally {
        this.requestingAccess = false;
      }
    },

    activeRuleCount() {
      return this.currentRules.filter((r) => !r.deleted).length;
    },

    canAddEntry() {
      const active = this.activeRuleCount();
      // Allow adding if under the limit, OR if grandfathered and below starting count (enables swaps)
      return (
        active < this.MAX_RULES_PER_CUSTOMER ||
        active < this.allowlistEntries.length
      );
    },

    startEditing() {
      // Copy current firewall rules to editable state
      const existingRules = this.allowlistEntries || [];
      this.currentRules = existingRules.map((rule) => ({
        id: rule.id,
        label: rule.label,
        ip_address: rule.ip_address,
      }));
      this.editingAllowlist = true;
    },

    cancelEditing() {
      this.currentRules = [];
      this.editingAllowlist = false;
    },

    addEntry() {
      if (!this.canAddEntry()) return;
      const tempId = `new-${Date.now()}-${Math.random()}`;
      this.currentRules.push({
        tempId: tempId,
        label: "",
        ip_address: "",
      });
      // Focus the newly added label input on next tick
      this.$nextTick(() => {
        const newInput = document.querySelector(
          `[data-rule-id="${tempId}"] input[name="label"]`,
        );
        if (newInput) {
          newInput.focus();
        }
      });
    },

    toggleRuleForDeletion(ruleId) {
      const rule = this.currentRules.find((r) => (r.id || r.tempId) === ruleId);
      if (rule) {
        // For new rules (no id), remove them immediately from the array
        if (!rule.id) {
          this.currentRules = this.currentRules.filter(
            (r) => (r.id || r.tempId) !== ruleId,
          );
        } else {
          // For existing rules, toggle the deleted flag
          rule.deleted = !rule.deleted;
        }
      }
    },

    isRuleMarkedForDeletion(ruleId) {
      const rule = this.currentRules.find((r) => (r.id || r.tempId) === ruleId);
      return rule?.deleted || false;
    },

    updateRule(ruleId, field, value) {
      const rule = this.currentRules.find((r) => (r.id || r.tempId) === ruleId);
      if (rule) {
        rule[field] = value;
      }
    },

    async saveChanges() {
      this.saving = true;

      try {
        // Filter out empty rules, deleted rules, and prepare the desired state
        const desiredRules = this.currentRules
          .filter((rule) => !rule.deleted && rule.label && rule.ip_address)
          .map((rule) => ({
            id: rule.id, // Will be undefined for new rules
            label: rule.label,
            ip_address: rule.ip_address,
          }));

        const requestBody = {
          rules: desiredRules,
        };

        // Submit changes to server
        const response = await fetch(
          "/admin/datacloud/dbaccess/update_allowlist",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": document
                .querySelector('meta[name="csrf-token"]')
                .getAttribute("content"),
            },
            body: JSON.stringify(requestBody),
          },
        );

        const responseData = await response.json().catch(() => null);

        if (response.ok) {
          // Update the local state with the response data
          if (responseData && responseData.rules) {
            this.allowlistEntries = responseData.rules;
          }

          this.editingAllowlist = false;
          this.currentRules = [];
          this.saving = false;

          // Show success notification using alert from response
          if (typeof pushNotification === "function" && responseData?.alert) {
            window.pushNotification({
              title: responseData.alert.title,
              desc: responseData.alert.desc,
            });
          }
        } else {
          // Show error notification using alert from response
          if (typeof pushNotification === "function" && responseData?.alert) {
            window.pushNotification({
              title: responseData.alert.title,
              desc: responseData.alert.desc,
              error: responseData.alert.error,
            });
          }
          const errorMessage =
            responseData?.error || "Failed to update IP addresses";
          throw new Error(errorMessage);
        }
      } catch (error) {
        console.error("Error updating IP addresses:", error);
        this.saving = false;
        alert(
          "An error occurred while updating the IP addresses. Please try again.",
        );
      }
    },
  };
}
